import { RGI } from '@/features/game-context/types';
import { sanitizeGameUrl } from '@/utils/sanitize-game-url';

/** Known Evolution Gaming CDN host suffixes (cert/stage/prod). */
const TRUSTED_EVOLUTION_HOST_SUFFIXES = [
  '.evo-test.com',
  '.evolution.com',
  '.evolutiongaming.com',
];

const extractHostname = (value: unknown): string | undefined => {
  const safeUrl = sanitizeGameUrl(
    typeof value === 'string' && !value.includes('://')
      ? `https://${value}`
      : value,
  );

  if (!safeUrl) {
    return undefined;
  }

  try {
    return new URL(safeUrl).hostname.toLowerCase();
  } catch {
    return undefined;
  }
};

const isTrustedEvolutionHost = (hostname: string): boolean =>
  TRUSTED_EVOLUTION_HOST_SUFFIXES.some(
    (suffix) => hostname === suffix.slice(1) || hostname.endsWith(suffix),
  );

type EvolutionLaunchDetails = {
  gameOrigin?: string;
  extraData?: {
    domainUrl?: string;
  };
};

/**
 * Resolves the Evolution mobile script host from supplier fields returned by GLS.
 * Rejects attacker-controlled hosts that are not on the Evolution CDN allowlist.
 */
const resolveCandidateHostname = (
  candidate: string,
  source: RGI,
): string | undefined => {
  const normalized =
    source === RGI.FGP ? candidate.split('//')[1] ?? candidate : candidate;

  return extractHostname(normalized);
};

export const resolveEvolutionScriptDomain = (
  launchDetails: EvolutionLaunchDetails,
  source: RGI,
): string | undefined => {
  const candidates: string[] = [];

  if (launchDetails.gameOrigin) {
    candidates.push(launchDetails.gameOrigin);
  }

  if (launchDetails.extraData?.domainUrl) {
    candidates.push(launchDetails.extraData.domainUrl);
  }

  return candidates
    .map((candidate) => resolveCandidateHostname(candidate, source))
    .find(
      (hostname): hostname is string =>
        Boolean(hostname && isTrustedEvolutionHost(hostname)),
    );
};
